import { createStoreContext } from "reusable/mobx-store-utils/storeUtils"
import AuthStore from "auth/AuthStore"
import { apiClient } from "api/trpc/trpc-client"
import Loader from "reusable/mobx-helpers/Loader"
import { UserOrgWithOrgDto } from "@gap/backend/app-services/user-org/models/UserOrgWithOrgDto"
import { orgContextCache } from "cache/orgContextCache"
import { UserTrialSetupStepEnum } from "@gap/backend/app-services/user/models/UserTrialStepDto"
import { defaultTrialOrgName } from "@gap/utils/utils"

interface SignUpAdvisorSelectOrgStoreProps {
   authStore: AuthStore
}

export default class SignUpAdvisorSelectOrgStore {
   sp
   loader = new Loader()
   selectedOrg: UserOrgWithOrgDto | undefined

   constructor(sp: SignUpAdvisorSelectOrgStoreProps) {
      this.sp = sp
   }

   get orgs() {
      return this.sp.authStore.userInfo?.orgs || []
   }

   get canSetupNewTrialOrg() {
      return !this.orgs.some(org => (org.orgType === 'advisor' && org.adminUserId === this.sp.authStore.userInfo?.id) || org.orgType === 'gap')
   }

   ensureUserInfoIsLoaded = async () => {
      await this.loader.executeLoading(async () => {
         if (!this.sp.authStore.userInfo) {
            // Ensure userInfo is loaded. Can occur during page reload
            this.sp.authStore.userInfo = await apiClient.user.getMyUserInfo.query()
         }
         this.selectedOrg = this.orgs[0]
         const defaultTrialOrg = this.orgs?.find(org => org.name === defaultTrialOrgName)
         if (defaultTrialOrg) {
            // Detect if we are setting up an org
            orgContextCache.authOrgId.set(defaultTrialOrg.orgId)
            await this.sp.authStore.updateUserAttributes({
               'custom:trialStep': UserTrialSetupStepEnum.orgSetup,
               'custom:trialOrgId': defaultTrialOrg.orgId
            })
            this.sp.authStore.sp.regionRouterStore.navigate(`/trial/org-setup`, true)
         }
      }, 'Loading user information...')
   }

   selectOrg = (org: UserOrgWithOrgDto) => {
      this.selectedOrg = org
   }

   navigateToSelectedOrg = () => {
      if (!this.selectedOrg) return
      orgContextCache.authOrgId.set(this.selectedOrg.orgId)
      this.sp.authStore.sp.regionRouterStore.navigate(`/org/${this.selectedOrg?.orgId}`, true)
   }

   setupNewTrialOrg = async () => {
      await this.loader.executeLoading(async () => {
         const trialOrg = await apiClient.org.createTrialOrg.mutate()
         orgContextCache.authOrgId.set(trialOrg.orgId)
         await this.sp.authStore.updateUserAttributes({
            'custom:trialStep': UserTrialSetupStepEnum.orgSetup,
            'custom:trialOrgId': trialOrg.orgId
         })
         this.sp.authStore.sp.regionRouterStore.navigate(`/trial/org-setup`, true)
      }, 'Setting you up as an advisor...')
   }
}

export const { Provider: SignUpAdvisorSelectOrgStoreProvider, useProvidedStore: useSignUpAdvisorSelectOrgStore } = createStoreContext(SignUpAdvisorSelectOrgStore)
